/**
 * Port of `src/components/Overlays2.js` from the App Builder export.
 *
 * Exposes a singleton `layerManager` with typed methods for turning
 * individual data layers on and off (cameras, guards, directory
 * kiosks, access control, temperature, assets, bluedot heatmap,
 * events, vacancy, elevators).
 *
 * Data sources, mirroring the `web-applet` / `directory-applet`
 * patterns:
 * - **Popular zones** (bluedot heatmap) go through the typed
 *   `AnalyticsClient` from `@venueos/analytics-sdk`, which proxies
 *   Snowflake reads via `/api/analytics/query`. The applet must hold
 *   the `analytics:read` permission for these to succeed.
 * - **Events** come from the venue's CMS-authored map data via the
 *   APIv1 `event` model (`/api/mappedin/apiv1/1/event?venue=<slug>`).
 *   That route is gated by `map:read`, not `analytics:read`. Events
 *   are fetched in React (see `useMapEvents`) and handed in here as
 *   plain data so the layer is decoupled from the network.
 */

import type { VenueOSClient } from '@mappedin/venueos-applet';
import type { AnalyticsClient } from '@venueos/analytics-sdk';
import { fetchDirectoryKiosks } from '../hooks/useDirectoryKiosks';
import type { MapEvent } from '../hooks/useMapEvents';
import { escapeHtml, getHeatmapColor } from '../lib/map-helpers';
import type {
  MappedinConnection,
  MappedinFloor,
  MappedinLocation,
  MappedinMapData,
  MappedinMapView,
  MappedinNode,
  MappedinSpace,
} from '../lib/mappedin-types';
import { useAppStore } from '../state/app-store';
import { clearFloorPipes } from './structural-pipes';

type MarkerKey =
  | 'cam'
  | 'dir'
  | 'guard'
  | 'acc'
  | 'tmp'
  | 'ast'
  | 'bluedot'
  | 'events'
  | 'vacancy'
  | 'elev';

interface GuardEntry {
  marker: unknown;
  cur: MappedinSpace;
  fid: string;
}

interface PopZoneRow {
  LOCATION_NAME?: string;
  location_name?: string;
  CNT?: number | string;
  cnt?: number | string;
}

interface LayerState {
  markers: Record<MarkerKey, unknown[]>;
  guards: GuardEntry[];
  vacancySpaces: MappedinSpace[];
  elevShapes: unknown[];
  guardStop: boolean;
  popCache: { slug: string | null; rows: PopZoneRow[] | null; loading: boolean };
  floorById: Record<string, MappedinFloor>;
  eOrd: Record<number, number>;
}

const state: LayerState = {
  markers: {
    cam: [],
    dir: [],
    guard: [],
    acc: [],
    tmp: [],
    ast: [],
    bluedot: [],
    events: [],
    vacancy: [],
    elev: [],
  },
  guards: [],
  vacancySpaces: [],
  elevShapes: [],
  guardStop: false,
  popCache: { slug: null, rows: null, loading: false },
  floorById: {},
  eOrd: {},
};

function circle(lat: number, lng: number, radiusM: number, steps = 16): unknown {
  const r = radiusM / 111320;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const coords: number[][] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (2 * Math.PI * i) / steps;
    coords.push([lng + (r * Math.cos(a)) / cosLat, lat + r * Math.sin(a)]);
  }
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [coords] },
      },
    ],
  };
}

interface MapHandles {
  v: MappedinMapView | null;
  d: MappedinMapData | null;
  f: MappedinFloor[];
}

function handles(): MapHandles {
  const s = useAppStore.getState();
  return { v: s.mapView, d: s.mapData, f: s.floors };
}

function seeded(arr: MappedinSpace[], n: number, seed: string): MappedinSpace[] {
  if (!arr.length) return [];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  const copy = arr.slice();
  for (let j = copy.length - 1; j > 0; j--) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    const k = h % (j + 1);
    const j_value = copy[j];
    const k_value = copy[k];
    if (j_value === undefined || k_value === undefined) continue;
    copy[j] = k_value;
    copy[k] = j_value;
  }
  return copy.slice(0, Math.min(n, copy.length));
}

function byFloor(d: MappedinMapData, floors: MappedinFloor[]): Record<string, MappedinSpace[]> {
  const all = d.getByType('space') as MappedinSpace[];
  const bf: Record<string, MappedinSpace[]> = {};
  for (const f of floors) bf[f.id] = [];
  for (const sp of all) {
    if (!sp.floor || !sp.center) continue;
    const bucket = bf[sp.floor.id];
    if (bucket) bucket.push(sp);
  }
  return bf;
}

function clearKey(k: MarkerKey): void {
  const mv = handles().v;
  if (mv) {
    for (const m of state.markers[k]) {
      try {
        mv.Markers.remove(m);
      } catch {
        // Already removed.
      }
    }
  }
  state.markers[k] = [];
}

function distance(a: MappedinSpace, b: MappedinSpace): number {
  const ac = a.center;
  const bc = b.center;
  if (!ac || !bc) return 1e9;
  const dx = (ac.longitude - bc.longitude) * 1e5;
  const dy = (ac.latitude - bc.latitude) * 1e5;
  return Math.sqrt(dx * dx + dy * dy);
}

async function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

const CAM_ICON =
  '<svg class="marker-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.934a.5.5 0 0 0-.777-.416L16 11"/><rect x="2" y="7" width="14" height="10" rx="2" ry="2"/></svg>';
const DIR_ICON =
  '<svg class="marker-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>';
const LOCK_ICON =
  '<svg class="marker-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
const UNLOCK_ICON =
  '<svg class="marker-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>';
const THERM_ICON =
  '<svg class="marker-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z"/></svg>';
const ELEV_ICON =
  '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="m9 8 3-3 3 3"/><path d="m9 16 3 3 3-3"/></svg>';
const CAL_ICON =
  '<svg class="marker-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>';

function showCameraMarkers(): void {
  const { v, d, f } = handles();
  if (!v || !d) return;
  clearKey('cam');
  const bf = byFloor(d, f);
  const names = [
    'CAM-N1',
    'CAM-S1',
    'CAM-E1',
    'CAM-W1',
    'CAM-C1',
    'CAM-C2',
    'CAM-ENT-A',
    'CAM-ENT-B',
  ];
  let ci = 0;
  const perFloor = Math.max(1, Math.ceil(names.length / Math.max(1, f.length)));
  let rs = 42;
  for (const fl of f) {
    const pool = bf[fl.id] ?? [];
    if (!pool.length) continue;
    const sel = seeded(pool, perFloor, `c-${fl.id}`);
    for (const sp of sel) {
      if (ci >= names.length) break;
      rs = (rs * 1103515245 + 12345) & 0x7fffffff;
      const on = (rs & 0xf) > 2;
      const sc = on ? '#22c55e' : '#ef4444';
      const name = names[ci];
      ci++;
      if (!name) continue;
      const html = `<div class="layer-marker layer-cam" style="display:flex;align-items:center;gap:4px;padding:3px 7px;border-radius:6px;background:rgba(245,158,11,0.9);color:#fff;font-size:9px;font-weight:600;white-space:nowrap;box-shadow:0 2px 8px rgba(245,158,11,0.4);pointer-events:auto;cursor:default">${CAM_ICON}<span class="marker-text">${name}</span><span class="marker-status" style="width:6px;height:6px;border-radius:50%;background:${sc};display:inline-block;box-shadow:0 0 4px ${sc}"></span></div>`;
      try {
        state.markers.cam.push(
          v.Markers.add(sp, html, { rank: 'always-visible', placement: 'top', occlude: true }),
        );
      } catch {
        // Ignore per original.
      }
    }
  }
}

function loadDirectoryKiosks(client: VenueOSClient | null, slug: string): void {
  const store = useAppStore.getState();
  store.setDirectoryKiosksLoading(true);
  const ctx = client?.context;
  if (!client || !ctx) {
    store.setDirectoryKiosks([]);
    return;
  }
  fetchDirectoryKiosks(client, ctx.org.id, slug)
    .then((kiosks) => store.setDirectoryKiosks(kiosks))
    .catch(() => store.setDirectoryKiosks([]));
}

function showDirectoryMarkers(): void {
  const { v, d, f } = handles();
  if (!v || !d) return;
  clearKey('dir');
  const kiosks = useAppStore.getState().directoryKiosks;
  if (!kiosks.length) return;
  const bf = byFloor(d, f);
  let ki = 0;
  for (let fi = 0; fi < f.length && ki < kiosks.length; fi++) {
    const fl = f[fi];
    if (!fl) continue;
    const pool = bf[fl.id] ?? [];
    if (!pool.length) continue;
    const perFloor = Math.max(1, Math.ceil((kiosks.length - ki) / (f.length - fi)));
    const sel = seeded(pool, perFloor, `d-${fl.id}`);
    for (const sp of sel) {
      if (ki >= kiosks.length) break;
      const kiosk = kiosks[ki];
      ki++;
      if (!kiosk) continue;
      const name = escapeHtml(String(kiosk.name ?? kiosk.kioskName ?? 'Directory'));
      const html = `<div class="layer-marker layer-dir" style="display:flex;align-items:center;gap:5px;padding:4px 8px;border-radius:6px;background:rgba(59,130,246,0.9);color:#fff;font-size:10px;font-weight:600;white-space:nowrap;box-shadow:0 2px 8px rgba(59,130,246,0.4);pointer-events:auto;cursor:default">${DIR_ICON}<span class="marker-text">${name}</span></div>`;
      try {
        state.markers.dir.push(
          v.Markers.add(sp, html, { rank: 'always-visible', placement: 'top', occlude: true }),
        );
      } catch {
        // Ignore.
      }
    }
  }
}

function neighborMap(spaces: MappedinSpace[], k: number): Record<string, MappedinSpace[]> {
  const m: Record<string, MappedinSpace[]> = {};
  for (const sp of spaces) {
    if (!sp.center) continue;
    const ds: Array<{ s: MappedinSpace; d: number }> = [];
    for (const other of spaces) {
      if (other === sp) continue;
      ds.push({ s: other, d: distance(sp, other) });
    }
    ds.sort((a, b) => a.d - b.d);
    m[sp.id] = ds.slice(0, k).map((x) => x.s);
  }
  return m;
}

function visibleSpaces(pool: MappedinSpace[]): MappedinSpace[] {
  const s = useAppStore.getState();
  const hidden = s.hiddenSpaceIds;
  const dyn = s.dynamicSpaceIds;
  return pool.filter((sp) => !hidden.has(sp.id) && !dyn.has(sp.id));
}

const GUARD_PHONETICS = [
  'Alpha',
  'Bravo',
  'Charlie',
  'Delta',
  'Echo',
  'Foxtrot',
  'Golf',
  'Hotel',
  'India',
  'Juliet',
];

function guardName(i: number): string {
  const prefix = GUARD_PHONETICS[Math.floor(i / 2) % GUARD_PHONETICS.length] ?? 'Officer';
  const suffix = (i % 2) + 1;
  return `${prefix}-${suffix}`;
}

const GUARD_AVATAR_COUNT = 5;

function guardAvatarUrl(name: string, fid: string): string {
  let h = 0;
  const s = `${name}|${fid}`;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  const idx = (Math.abs(h) % GUARD_AVATAR_COUNT) + 1;
  return `/officers/officer-${idx}.png`;
}

function buildingCount(floors: MappedinFloor[]): number {
  const elevCount: Record<number, number> = {};
  for (const fl of floors) {
    const e = fl.elevation ?? 0;
    elevCount[e] = (elevCount[e] ?? 0) + 1;
  }
  return Math.max(1, ...Object.values(elevCount));
}

function guardHtml(name: string, avatarUrl: string): string {
  return `<div class="layer-marker layer-guard guard-marker" title="${escapeHtml(name)}" style="pointer-events:auto;cursor:default"><img class="guard-avatar" src="${avatarUrl}" alt="${escapeHtml(name)}" /></div>`;
}

interface PlaceFloorCtx {
  v: MappedinMapView;
  fl: MappedinFloor;
  pool: MappedinSpace[];
  target: number;
  startIndex: number;
  nm: Record<string, Record<string, MappedinSpace[]>>;
}

function placeGuardsOnFloor(ctx: PlaceFloorCtx): number {
  const { v, fl, pool, target, startIndex, nm } = ctx;
  if (target <= 0) return 0;
  const rooms = pool.filter((sp) => (sp.type ?? '').toLowerCase() === 'room');
  const ranked = rooms.length > 0 ? rooms : pool;
  const candidates = seeded(ranked, Math.min(ranked.length, target * 4), `g-${fl.id}`);
  const used = new Set<string>();
  let placed = 0;
  for (const start of candidates) {
    if (placed >= target) break;
    if (used.has(start.id)) continue;
    const idx = startIndex + placed;
    try {
      const name = guardName(idx);
      const mk = v.Markers.add(start, guardHtml(name, guardAvatarUrl(name, fl.id)), {
        rank: 'always-visible',
        placement: 'top',
        occlude: true,
      });
      const entry: GuardEntry = { marker: mk, cur: start, fid: fl.id };
      state.guards.push(entry);
      state.markers.guard.push(mk);
      used.add(start.id);
      void patrol(entry, nm, (idx + 1) * 1200);
      placed++;
    } catch {
      // Try the next candidate space.
    }
  }
  return placed;
}

function showGuardMarkers(): void {
  const { v, d, f } = handles();
  if (!v || !d) return;
  clearKey('guard');
  state.guardStop = false;
  state.guards = [];
  const bf = byFloor(d, f);
  // Restrict candidates to spaces that are actually rendered. Markers
  // anchored to hidden/dynamic spaces still get added by the SDK but
  // are culled visually, which is why some venues showed no guards.
  const visible: Record<string, MappedinSpace[]> = {};
  for (const fl of f) visible[fl.id] = visibleSpaces(bf[fl.id] ?? []);
  const nm: Record<string, Record<string, MappedinSpace[]>> = {};
  for (const fl of f) {
    const p = visible[fl.id] ?? [];
    if (p.length >= 2) nm[fl.id] = neighborMap(p, 8);
  }
  const usableFloors = f.filter((fl) => (visible[fl.id]?.length ?? 0) > 0);
  if (usableFloors.length === 0) return;
  // Scale total guards with floor count: ~1.75/floor (within the
  // 1.5-2/floor range), rounded down, with a minimum of 1. Capped at
  // 5 per building so taller venues don't drown in patrol markers.
  const cap = buildingCount(f) * 5;
  const totalGuards = Math.min(cap, Math.max(1, Math.floor(usableFloors.length * 1.75)));
  // Largest floors first so dense floors absorb the extra guard when
  // the per-floor count rounds unevenly.
  const orderedFloors = usableFloors
    .slice()
    .sort((a, b) => (visible[b.id]?.length ?? 0) - (visible[a.id]?.length ?? 0));
  let remaining = totalGuards;
  let remainingFloors = orderedFloors.length;
  let gi = 0;
  for (const fl of orderedFloors) {
    if (remaining <= 0) break;
    const pool = visible[fl.id] ?? [];
    const target = Math.min(pool.length, Math.ceil(remaining / remainingFloors));
    remainingFloors--;
    const placed = placeGuardsOnFloor({ v, fl, pool, target, startIndex: gi, nm });
    gi += placed;
    remaining -= placed;
  }
}

async function patrol(
  guard: GuardEntry,
  nm: Record<string, Record<string, MappedinSpace[]>>,
  delay: number,
): Promise<void> {
  await wait(delay + Math.random() * 1000);
  while (!state.guardStop) {
    const mv = handles().v;
    if (!mv) return;
    const nb = nm[guard.fid];
    if (!nb) {
      await wait(3000);
      continue;
    }
    const near = nb[guard.cur.id];
    if (!near || !near.length) {
      await wait(2000);
      continue;
    }
    const next = near[Math.floor(Math.random() * near.length)];
    if (!next) {
      await wait(2000);
      continue;
    }
    try {
      const markers = mv.Markers as MappedinMapView['Markers'] & {
        animateTo?: (m: unknown, t: unknown, opts?: Record<string, unknown>) => void;
      };
      markers.animateTo?.(guard.marker, next, { duration: 3000 });
    } catch {
      // Ignore.
    }
    guard.cur = next;
    await wait(3000);
    if (state.guardStop) return;
    await wait(1500 + Math.random() * 2500);
  }
}

interface HasNodes {
  nodes?: MappedinNode[];
  name?: string;
  center?: { latitude: number; longitude: number };
}

function findEntrances(md: MappedinMapData): MappedinLocation[] {
  const entrances: MappedinLocation[] = [];
  const types = ['enterprise-location', 'location-profile', 'location', 'point-of-interest'];
  for (const t of types) {
    try {
      const locs = (md.getByType(t) as MappedinLocation[]) ?? [];
      for (const loc of locs) {
        const nm = ((loc.name ?? '') + ' ' + (loc.type ?? '')).toLowerCase();
        if (
          nm.includes('entrance') ||
          nm.includes('entry') ||
          nm.includes('door') ||
          nm.includes('gate') ||
          nm.includes('exit')
        ) {
          entrances.push(loc);
        }
      }
    } catch {
      // Ignore.
    }
  }
  return entrances;
}

function buildAccessTargets(entrances: MappedinLocation[]): Array<{
  anchor: unknown;
  name: string;
}> {
  const targets: Array<{ anchor: unknown; name: string }> = [];
  for (const ent of entrances) {
    const entAsNodes = ent as HasNodes;
    const nodes = entAsNodes.nodes ?? [];
    if (nodes.length > 0) {
      for (const n of nodes) targets.push({ anchor: n, name: ent.name ?? 'Entrance' });
    } else if (entAsNodes.center) {
      targets.push({ anchor: ent, name: ent.name ?? 'Entrance' });
    }
  }
  return targets;
}

function accessMarkerHtml(label: string, locked: boolean): string {
  const bg = locked ? 'rgba(239,68,68,0.85)' : 'rgba(34,197,94,0.85)';
  const sh = locked ? 'rgba(239,68,68,0.4)' : 'rgba(34,197,94,0.4)';
  return `<div class="layer-marker" style="display:flex;align-items:center;gap:4px;padding:3px 7px;border-radius:6px;background:${bg};color:#fff;font-size:9px;font-weight:600;white-space:nowrap;box-shadow:0 2px 8px ${sh};pointer-events:auto;cursor:default">${locked ? LOCK_ICON : UNLOCK_ICON}<span class="marker-text">${label}</span><span class="marker-status" style="font-size:8px;opacity:0.85">${locked ? 'Locked' : 'Open'}</span></div>`;
}

function placeAccessMarker(v: MappedinMapView, anchor: unknown, html: string): void {
  try {
    state.markers.acc.push(
      v.Markers.add(anchor, html, {
        rank: 'always-visible',
        placement: 'top',
        occlude: true,
      }),
    );
  } catch {
    // Ignore.
  }
}

function showAccessMarkersForEntrances(
  v: MappedinMapView,
  targets: Array<{ anchor: unknown; name: string }>,
): void {
  let rs = 77;
  targets.forEach((tgt, i) => {
    rs = (rs * 1103515245 + 12345) & 0x7fffffff;
    const lk = (rs & 0x3) === 0;
    const label = `${escapeHtml(tgt.name)} ${i + 1}`;
    placeAccessMarker(v, tgt.anchor, accessMarkerHtml(label, lk));
  });
}

const FAKE_DOORS = [
  { n: 'Main Entrance', lk: false },
  { n: 'Loading Dock', lk: true },
  { n: 'Staff Entry', lk: false },
  { n: 'Emergency Exit A', lk: true },
  { n: 'Parking Gate', lk: false },
  { n: 'Service Door B', lk: true },
];

function showAccessMarkersFallback(
  v: MappedinMapView,
  d: MappedinMapData,
  f: MappedinFloor[],
): void {
  const bf = byFloor(d, f);
  const perFloor = Math.max(1, Math.ceil(FAKE_DOORS.length / Math.max(1, f.length)));
  let di = 0;
  for (const fl of f) {
    if (di >= FAKE_DOORS.length) break;
    const pool = bf[fl.id] ?? [];
    if (!pool.length) continue;
    const sel = seeded(pool, perFloor, `ac-${fl.id}`);
    for (const sp of sel) {
      if (di >= FAKE_DOORS.length) break;
      const door = FAKE_DOORS[di];
      di++;
      if (!door) continue;
      placeAccessMarker(v, sp, accessMarkerHtml(escapeHtml(door.n), door.lk));
    }
  }
}

function showAccessMarkers(): void {
  const { v, d, f } = handles();
  if (!v || !d) return;
  clearKey('acc');
  const entrances = findEntrances(d);
  const targets = buildAccessTargets(entrances);
  if (targets.length > 0) {
    showAccessMarkersForEntrances(v, targets);
  } else {
    showAccessMarkersFallback(v, d, f);
  }
}

function showTempMarkers(): void {
  const { v, d, f } = handles();
  if (!v || !d) return;
  clearKey('tmp');
  const bf = byFloor(d, f);
  const sns: Array<{ n: string; t: number; st: 'ok' | 'warn' | 'cool' }> = [
    { n: 'HVAC Zone A', t: 72, st: 'ok' },
    { n: 'HVAC Zone B', t: 68, st: 'ok' },
    { n: 'Server Room', t: 81, st: 'warn' },
    { n: 'Food Court', t: 74, st: 'ok' },
    { n: 'Storage West', t: 64, st: 'cool' },
    { n: 'Mech Room', t: 86, st: 'warn' },
  ];
  let ti = 0;
  const perFloor = Math.max(1, Math.ceil(sns.length / Math.max(1, f.length)));
  for (const fl of f) {
    if (ti >= sns.length) break;
    const pool = bf[fl.id] ?? [];
    if (!pool.length) continue;
    const sel = seeded(pool, perFloor, `tp-${fl.id}`);
    for (const sp of sel) {
      if (ti >= sns.length) break;
      const t = sns[ti];
      ti++;
      if (!t) continue;
      const bg =
        t.st === 'warn'
          ? 'rgba(245,158,11,0.9)'
          : t.st === 'cool'
            ? 'rgba(56,189,248,0.85)'
            : 'rgba(20,184,166,0.85)';
      const sh = t.st === 'warn' ? 'rgba(245,158,11,0.4)' : 'rgba(20,184,166,0.4)';
      const html = `<div class="layer-marker" style="display:flex;align-items:center;gap:4px;padding:3px 7px;border-radius:6px;background:${bg};color:#fff;font-size:9px;font-weight:600;white-space:nowrap;box-shadow:0 2px 8px ${sh};pointer-events:auto;cursor:default">${THERM_ICON}<span class="marker-text">${escapeHtml(t.n)}</span><span class="marker-status" style="font-weight:700;font-size:10px">${t.t} F</span></div>`;
      try {
        state.markers.tmp.push(
          v.Markers.add(sp, html, {
            rank: 'always-visible',
            placement: 'top',
            occlude: true,
          }),
        );
      } catch {
        // Ignore.
      }
    }
  }
}

function showAssetMarkers(): void {
  const { v, d, f } = handles();
  if (!v || !d) return;
  clearKey('ast');
  const bf = byFloor(d, f);
  const lbSvg =
    '<svg class="marker-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/><circle cx="12" cy="16" r="1"/></svg>';
  const spSvg =
    '<svg class="marker-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"/></svg>';
  const emSvg =
    '<svg class="marker-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="16" height="12" x="4" y="6" rx="2"/><path d="M9 12h6"/><path d="M12 9v6"/></svg>';
  const items: Array<{ n: string; sv: string; tp: 'lb' | 'sp' | 'em' }> = [
    { n: 'Lockbox A', sv: lbSvg, tp: 'lb' },
    { n: 'Standpipe 1', sv: spSvg, tp: 'sp' },
    { n: 'Emergency Kit', sv: emSvg, tp: 'em' },
    { n: 'Lockbox B', sv: lbSvg, tp: 'lb' },
    { n: 'Standpipe 2', sv: spSvg, tp: 'sp' },
    { n: 'First Aid Station', sv: emSvg, tp: 'em' },
  ];
  let ai = 0;
  const perFloor = Math.max(1, Math.ceil(items.length / Math.max(1, f.length)));
  for (const fl of f) {
    if (ai >= items.length) break;
    const pool = bf[fl.id] ?? [];
    if (!pool.length) continue;
    const sel = seeded(pool, perFloor, `as-${fl.id}`);
    for (const sp of sel) {
      if (ai >= items.length) break;
      const it = items[ai];
      ai++;
      if (!it) continue;
      const bg =
        it.tp === 'em'
          ? 'rgba(239,68,68,0.85)'
          : it.tp === 'sp'
            ? 'rgba(56,189,248,0.85)'
            : 'rgba(161,98,247,0.85)';
      const sh =
        it.tp === 'em'
          ? 'rgba(239,68,68,0.4)'
          : it.tp === 'sp'
            ? 'rgba(56,189,248,0.4)'
            : 'rgba(161,98,247,0.4)';
      const html = `<div class="layer-marker" style="display:flex;align-items:center;gap:4px;padding:3px 7px;border-radius:6px;background:${bg};color:#fff;font-size:9px;font-weight:600;white-space:nowrap;box-shadow:0 2px 8px ${sh};pointer-events:auto;cursor:default">${it.sv}<span class="marker-text">${escapeHtml(it.n)}</span></div>`;
      try {
        state.markers.ast.push(
          v.Markers.add(sp, html, {
            rank: 'always-visible',
            placement: 'top',
            occlude: true,
          }),
        );
      } catch {
        // Ignore.
      }
    }
  }
}

/**
 * Bluedot heatmap query: aggregate `session_events_view` by the
 * resolved `LOCATION_NAME` payload over the last 90 days for a single
 * venue, so we can render rank markers on the loaded MVF's spaces /
 * enterprise-locations. Uses the lowercase `analytics.*` schema and
 * `TIMESTAMP_WALL` to match how `web-applet` and `directory-applet`
 * shape their queries.
 */
const POP_SQL = `
  SELECT
    DATA:payload.locationName::STRING AS LOCATION_NAME,
    COUNT(*) AS CNT
  FROM analytics.session_events_view
  WHERE "VENUE" = ?
    AND "TIMESTAMP_WALL" >= DATEADD('day', -90, CURRENT_TIMESTAMP())
    AND DATA:payload.locationName IS NOT NULL
  GROUP BY DATA:payload.locationName
  ORDER BY CNT DESC
  LIMIT 15
`;

async function fetchPopularZones(
  analyticsClient: AnalyticsClient,
  slug: string,
): Promise<PopZoneRow[]> {
  try {
    const result = await analyticsClient.query(POP_SQL, [slug]);
    const rows: PopZoneRow[] = [];
    for (const raw of result.data) {
      rows.push(raw as PopZoneRow);
    }
    return rows;
  } catch {
    return [];
  }
}

function prefetchPopularZones(analyticsClient: AnalyticsClient | null, slug: string | null): void {
  if (!analyticsClient || !slug) return;
  if (state.popCache.slug === slug || state.popCache.loading) return;
  state.popCache.loading = true;
  state.popCache.slug = slug;
  void fetchPopularZones(analyticsClient, slug).then((rows) => {
    state.popCache.rows = rows;
    state.popCache.loading = false;
  });
}

interface LocMaps {
  spaceMap: Record<string, MappedinSpace>;
  spaceById: Record<string, MappedinSpace>;
  locMap: Record<string, MappedinLocation>;
  locById: Record<string, MappedinLocation>;
}

function buildLocMaps(d: MappedinMapData): LocMaps {
  const allSpaces = (d.getByType('space') as MappedinSpace[]) ?? [];
  const spaceMap: Record<string, MappedinSpace> = {};
  const spaceById: Record<string, MappedinSpace> = {};
  for (const sp of allSpaces) {
    if (sp.id && sp.center) spaceById[sp.id] = sp;
    if (sp.name && sp.center) spaceMap[sp.name.toLowerCase().trim()] = sp;
  }
  let locs: MappedinLocation[] = [];
  try {
    locs = (d.getByType('enterprise-location') as MappedinLocation[]) ?? [];
  } catch {
    // Ignore.
  }
  if (!locs.length) {
    try {
      locs = (d.getByType('location-profile') as MappedinLocation[]) ?? [];
    } catch {
      // Ignore.
    }
  }
  const locMap: Record<string, MappedinLocation> = {};
  const locById: Record<string, MappedinLocation> = {};
  for (const loc of locs) {
    if (loc.id) locById[loc.id] = loc;
    if (loc.name) locMap[loc.name.toLowerCase().trim()] = loc;
  }
  return { spaceMap, spaceById, locMap, locById };
}

function numberOf(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return Number(v) || 0;
  return 0;
}

function renderPopZones(rows: PopZoneRow[]): void {
  const { v, d } = handles();
  if (!v || !d || !rows.length) return;
  clearKey('bluedot');
  const maps = buildLocMaps(d);
  const maxCnt = numberOf(rows[0]?.CNT ?? rows[0]?.cnt) || 1;
  let placed = 0;
  for (const row of rows) {
    const name = String(row.LOCATION_NAME ?? row.location_name ?? '');
    const cnt = numberOf(row.CNT ?? row.cnt);
    if (!name || !cnt) continue;
    const key = name.toLowerCase().trim();
    const target = maps.spaceMap[key] ?? maps.locMap[key];
    if (!target) continue;
    const intensity = Math.min(1, cnt / maxCnt);
    const size = Math.round(24 + intensity * 28);
    const pulseSize = Math.round(size * 2.2);
    const color = getHeatmapColor(intensity);
    const rank = placed < 5 ? String(placed + 1) : '';
    const inner =
      `<div style="position:relative;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center">` +
      `<div style="position:absolute;width:${pulseSize}px;height:${pulseSize}px;border-radius:50%;background:${color};opacity:0.15;animation:pop-pulse 2s ease-in-out infinite"></div>` +
      `<div style="position:absolute;width:${Math.round(pulseSize * 0.7)}px;height:${Math.round(pulseSize * 0.7)}px;border-radius:50%;background:${color};opacity:0.25;animation:pop-pulse 2s ease-in-out 0.3s infinite"></div>` +
      `<div style="position:relative;z-index:1;width:${size}px;height:${size}px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 12px ${color}66;border:2px solid rgba(255,255,255,0.25)">` +
      `<span style="color:#fff;font-size:${rank ? '11' : '9'}px;font-weight:700">${rank || cnt}</span></div></div>` +
      `<div class="marker-text" style="position:absolute;top:100%;left:50%;transform:translateX(-50%);margin-top:2px;white-space:nowrap;font-size:8px;font-weight:600;color:#fff;background:rgba(0,0,0,0.6);padding:1px 5px;border-radius:3px;pointer-events:none">${escapeHtml(name)} (${cnt})</div>`;
    const html = `<div style="position:relative;display:flex;flex-direction:column;align-items:center;pointer-events:auto;cursor:default">${inner}</div>`;
    try {
      state.markers.bluedot.push(
        v.Markers.add(target, html, {
          rank: 'always-visible',
          placement: 'top',
          occlude: true,
        }),
      );
      placed++;
    } catch {
      // Ignore.
    }
  }
}

function showBluedotHeatmap(analyticsClient: AnalyticsClient | null): void {
  const { v, d } = handles();
  if (!v || !d) return;
  clearKey('bluedot');
  if (state.popCache.rows && state.popCache.rows.length) {
    renderPopZones(state.popCache.rows);
    return;
  }
  const venue = useAppStore.getState().selectedVenue;
  if (!venue?.slug || !analyticsClient) return;
  state.popCache.loading = true;
  state.popCache.slug = venue.slug;
  void fetchPopularZones(analyticsClient, venue.slug).then((rows) => {
    state.popCache.rows = rows;
    state.popCache.loading = false;
    renderPopZones(rows);
  });
}

/**
 * Coerce one of the APIv1 date fields (`startDate`, `endDate`, etc.)
 * into a Unix epoch in ms. APIv1 returns these as numbers in our
 * sample payloads, but older entries can also be ISO strings, so we
 * accept both. Returns `null` when the value is missing or unparseable
 * so callers can decide how to handle "no bound" cases.
 */
function eventEpochMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/**
 * An event is "currently active" when now is within `[startDate, endDate]`.
 * Missing bounds are treated as open-ended so e.g. an evergreen
 * announcement with no `endDate` is still considered active. We do
 * NOT exclude future-start-dated events (they're upcoming, not
 * expired) — the explicit user requirement is just to drop expired
 * ones, i.e. events whose `endDate` is in the past.
 */
function isEventActive(event: MapEvent, nowMs: number): boolean {
  const endMs = eventEpochMs(event.endDate);
  return endMs === null || endMs >= nowMs;
}

type EventLocRef = { id?: string; name?: string };

function refFromStringOrObject(entry: unknown): EventLocRef | null {
  if (typeof entry === 'string' && entry) return { id: entry };
  if (!entry || typeof entry !== 'object') return null;
  const obj = entry as { id?: unknown; name?: unknown };
  const ref: EventLocRef = {};
  if (typeof obj.id === 'string') ref.id = obj.id;
  if (typeof obj.name === 'string') ref.name = obj.name;
  return ref.id || ref.name ? ref : null;
}

function flattenedLocationRef(event: MapEvent): EventLocRef | null {
  if (typeof event.locationName === 'string' && event.locationName) {
    const ref: EventLocRef = { name: event.locationName };
    if (typeof event.locationId === 'string' && event.locationId) ref.id = event.locationId;
    return ref;
  }
  if (typeof event.locationId === 'string' && event.locationId) {
    return { id: event.locationId };
  }
  return null;
}

/**
 * Collect every location reference (name and/or id) on an APIv1 event
 * payload. APIv1 returns relations in any of: a flattened
 * `locationName`/`locationId` pair, a singular `location` (string ID or
 * `{ id, name }` object), or a `locations` array of strings/objects.
 * Returning a list lets the renderer try several anchors per event and
 * place a marker on the first one that resolves to an SDK target.
 */
function collectEventLocationRefs(event: MapEvent): EventLocRef[] {
  const refs: EventLocRef[] = [];
  const flattened = flattenedLocationRef(event);
  if (flattened) refs.push(flattened);

  const single = refFromStringOrObject(event.location);
  if (single) refs.push(single);

  if (Array.isArray(event.locations)) {
    for (const entry of event.locations) {
      const ref = refFromStringOrObject(entry);
      if (ref) refs.push(ref);
    }
  }
  return refs;
}

interface EventTarget {
  anchor: unknown;
  key: string;
}

/**
 * `Markers.add()` only accepts Door, Space, Coordinate, or Node. When
 * the event resolver lands on a Location object (Enterprise or Maker),
 * the marker call would throw silently. Unwrap the location to its
 * first Space (preferred — gives the marker a real footprint to attach
 * to) and fall back to the first Node, returning the original object
 * if neither is present so the caller can decide whether to skip it.
 */
function unwrapLocationAnchor(loc: MappedinLocation | MappedinSpace): unknown {
  if ('center' in loc && loc.center) return loc;
  const asLoc = loc as MappedinLocation;
  if (Array.isArray(asLoc.spaces)) {
    for (const sp of asLoc.spaces) {
      if (sp?.center) return sp;
    }
  }
  if (Array.isArray(asLoc.nodes)) {
    for (const n of asLoc.nodes) {
      if (n?.coordinate) return n;
    }
  }
  return loc;
}

/**
 * Mappedin APIv1 hands back raw 24-char Mongo ObjectIds for relation
 * fields (`location: "5c3824a745ef9046f9a7a57b"`), but the SDK's MVF
 * prefixes those same IDs by entity type when it loads them
 * (`el_5c3824...` for enterprise locations, `s_...` for spaces, etc.).
 * To resolve an APIv1 location ID against an SDK-loaded location we
 * have to try every known form: the bare ID first (in case a future
 * MVF drops the prefix), then each entity-prefixed variant.
 */
function idCandidates(rawId: string): string[] {
  const trimmed = rawId.trim();
  if (!trimmed) return [];
  const stripped = trimmed.replace(/^(el_|lp_|s_|n_|p_|c_|ec_|lc_)/, '');
  const out = new Set<string>([trimmed, stripped]);
  for (const prefix of ['el_', 'lp_', 's_']) out.add(prefix + stripped);
  return Array.from(out);
}

/**
 * Resolve the first usable SDK anchor for an event. We prefer matching
 * by lower-cased name (which lines up with both space `name` and
 * location `name` in the MVF), then fall back to ID lookup against
 * EnterpriseLocation/LocationProfile/Space — APIv1 IDs are stable and
 * the SDK exposes them, so an event whose `location` field is just an
 * opaque ID still gets pinned correctly.
 */
function resolveEventAnchor(refs: EventLocRef[], maps: LocMaps): EventTarget | null {
  for (const ref of refs) {
    if (ref.name) {
      const key = ref.name.toLowerCase().trim();
      const byName = maps.spaceMap[key] ?? maps.locMap[key];
      if (byName) return { anchor: unwrapLocationAnchor(byName), key };
    }
    if (ref.id) {
      for (const candidate of idCandidates(ref.id)) {
        const byId = maps.locById[candidate] ?? maps.spaceById[candidate];
        if (byId) {
          const key = byId.name ? byId.name.toLowerCase().trim() : `id:${candidate}`;
          return { anchor: unwrapLocationAnchor(byId), key };
        }
      }
    }
  }
  return null;
}

function eventMarkerHtml(name: string): string {
  // Match the layout used by every other marker on the map (camera,
  // directory, access, temperature, etc.): a `.layer-marker` chip
  // containing the inline `marker-icon` SVG followed by a
  // `.marker-text` span. The shared `.markers-compact` rules in
  // styles.css collapse the chip to an icon-only circle when the
  // camera is zoomed out, then expand it back to icon+label when
  // zoomed past the legend's threshold. The legend's
  // `camera-change` listener already toggles that root class, so we
  // get zoom-aware label visibility for free.
  return `<div class="layer-marker layer-events" style="display:flex;align-items:center;gap:5px;padding:4px 8px;border-radius:6px;background:rgba(139,92,246,0.9);color:#fff;font-size:10px;font-weight:600;white-space:nowrap;box-shadow:0 2px 8px rgba(139,92,246,0.4);pointer-events:auto;cursor:default">${CAL_ICON}<span class="marker-text">${escapeHtml(name)}</span></div>`;
}

interface EventsDebug {
  total: number;
  expired: number;
  noRefs: number;
  unresolvedRefs: number;
  duplicates: number;
  markerErrors: number;
  firstMarkerError: string | null;
  firstAnchorTypeName: string | null;
  firstUnresolved: { event: MapEvent; refs: EventLocRef[] } | null;
  knownLocIds: string[];
  candidatesTried: string[];
}

function placeEventMarker(
  v: MappedinMapView,
  anchor: unknown,
  name: string,
  dbg: EventsDebug,
): boolean {
  if (!dbg.firstAnchorTypeName) {
    dbg.firstAnchorTypeName =
      anchor && typeof anchor === 'object' ? anchor.constructor.name : typeof anchor;
  }
  const html = eventMarkerHtml(name);
  try {
    state.markers.events.push(
      v.Markers.add(anchor, html, {
        rank: 'always-visible',
        placement: 'top',
        occlude: true,
      }),
    );
    return true;
  } catch (err: unknown) {
    dbg.markerErrors++;
    if (!dbg.firstMarkerError) {
      dbg.firstMarkerError = err instanceof Error ? err.message : String(err);
    }
    return false;
  }
}

function tryRenderEvent(
  event: MapEvent,
  maps: LocMaps,
  used: Record<string, boolean>,
  v: MappedinMapView,
  nowMs: number,
  dbg: EventsDebug,
): boolean {
  const name = String(event.name ?? '').trim();
  if (!name) return false;
  if (!isEventActive(event, nowMs)) {
    dbg.expired++;
    return false;
  }
  const refs = collectEventLocationRefs(event);
  if (refs.length === 0) {
    dbg.noRefs++;
    return false;
  }
  const target = resolveEventAnchor(refs, maps);
  if (!target) {
    dbg.unresolvedRefs++;
    if (!dbg.firstUnresolved) {
      dbg.firstUnresolved = { event, refs };
      dbg.candidatesTried = refs.flatMap((r) => (r.id ? idCandidates(r.id) : []));
    }
    return false;
  }
  if (used[target.key]) {
    dbg.duplicates++;
    return false;
  }
  used[target.key] = true;
  return placeEventMarker(v, target.anchor, name, dbg);
}

function renderEvents(events: MapEvent[]): void {
  const { v, d } = handles();
  if (!v || !d || !events.length) return;
  clearKey('events');
  const maps = buildLocMaps(d);
  const dbg: EventsDebug = {
    total: events.length,
    expired: 0,
    noRefs: 0,
    unresolvedRefs: 0,
    duplicates: 0,
    markerErrors: 0,
    firstMarkerError: null,
    firstAnchorTypeName: null,
    firstUnresolved: null,
    knownLocIds: Object.keys(maps.locById).slice(0, 5),
    candidatesTried: [],
  };
  const nowMs = Date.now();
  let placed = 0;
  const used: Record<string, boolean> = {};
  for (const event of events) {
    if (tryRenderEvent(event, maps, used, v, nowMs, dbg)) placed++;
    if (placed >= 8) break;
  }
  // Stash diagnostics on `globalThis` so they're inspectable from the
  // browser console (`__venueOverviewEventsDebug`) even if some
  // intermediate tool strips inline `console.info` calls. Logging the
  // stringified result keeps the data inline in the console row so it
  // can't get collapsed.
  (globalThis as Record<string, unknown>)['__venueOverviewEventsDebug'] = dbg;
  if (placed === 0 && events.length > 0) {
    console.info(
      '[venue-overview]',
      JSON.stringify({
        total: dbg.total,
        expired: dbg.expired,
        noRefs: dbg.noRefs,
        unresolvedRefs: dbg.unresolvedRefs,
        duplicates: dbg.duplicates,
        markerErrors: dbg.markerErrors,
        firstMarkerError: dbg.firstMarkerError,
        firstAnchorTypeName: dbg.firstAnchorTypeName,
        firstUnresolvedLocation: dbg.firstUnresolved?.event.location ?? null,
        firstUnresolvedRefs: dbg.firstUnresolved?.refs ?? null,
        candidatesTried: dbg.candidatesTried,
        knownLocIds: dbg.knownLocIds,
      }),
    );
  }
}

function showEvents(events: MapEvent[]): void {
  const { v, d } = handles();
  if (!v || !d) return;
  clearKey('events');
  if (!events.length) return;
  renderEvents(events);
}

function showVacancy(): void {
  const { v, d, f } = handles();
  if (!v || !d) return;
  clearVacancy();
  const bf = byFloor(d, f);
  const pool: MappedinSpace[] = [];
  for (const fl of f) {
    const list = bf[fl.id] ?? [];
    for (const sp of list) {
      if (sp.name && sp.name !== 'room') pool.push(sp);
    }
  }
  const picked = seeded(
    pool,
    Math.min(8, Math.max(3, Math.round(pool.length * 0.04))),
    `vac-${Date.now()}`,
  );
  state.vacancySpaces = [];
  for (const sp of picked) {
    state.vacancySpaces.push(sp);
    try {
      v.updateState(sp, { color: '#fb923c', opacity: 0.85 });
    } catch {
      // Ignore.
    }
  }
}

function clearVacancy(): void {
  const { v } = handles();
  if (v && state.vacancySpaces.length) {
    for (const sp of state.vacancySpaces) {
      try {
        v.updateState(sp, { color: undefined, opacity: 1.0 });
      } catch {
        // Ignore.
      }
    }
  }
  state.vacancySpaces = [];
  clearKey('vacancy');
}

function rebuildFloorLookup(): void {
  const floors = useAppStore.getState().floors;
  state.floorById = {};
  state.eOrd = {};
  const elevs: number[] = [];
  const seen: Record<number, boolean> = {};
  for (const fl of floors) {
    state.floorById[fl.id] = fl;
    const ev = fl.elevation ?? 0;
    if (!seen[ev]) {
      seen[ev] = true;
      elevs.push(ev);
    }
  }
  elevs.sort((a, b) => a - b);
  elevs.forEach((e, i) => {
    state.eOrd[e] = i;
  });
}

interface ElevNode extends MappedinNode {
  floor?: MappedinFloor;
}

interface ElevatorStatus {
  label: string;
  color: string;
  bg: string;
  sh: string;
}

interface SortedElevNode {
  node: ElevNode;
  ord: number;
  floor: MappedinFloor | null;
}

function sortElevatorNodes(nodes: ElevNode[]): SortedElevNode[] {
  const sorted: SortedElevNode[] = [];
  for (const nd of nodes) {
    const fl = nd.floor ? (state.floorById[nd.floor.id] ?? null) : null;
    const ord = fl ? (state.eOrd[fl.elevation ?? 0] ?? 0) : 0;
    sorted.push({ node: nd, ord, floor: fl });
  }
  sorted.sort((a, b) => a.ord - b.ord);
  return sorted;
}

function maybeAddShaftShape(
  v: MappedinMapView,
  bottom: SortedElevNode,
  floorSpan: number,
  st: ElevatorStatus,
): void {
  const store = useAppStore.getState();
  if (!store.isStacked || floorSpan <= 0 || !bottom.node.coordinate || !v.Shapes) return;
  const { latitude, longitude } = bottom.node.coordinate;
  const circleGeo = circle(latitude, longitude, 1.4, 16);
  const shaftHeight = Math.max(0.1, floorSpan * store.floorGap);
  try {
    const shape = v.Shapes.add(
      circleGeo,
      {
        color: st.color,
        altitude: 0,
        height: shaftHeight,
        opacity: 0.65,
        interactive: false,
        renderOrder: -1000,
      },
      bottom.floor,
    );
    state.elevShapes.push(shape);
  } catch {
    // Ignore.
  }
}

function addElevatorDots(v: MappedinMapView, sorted: SortedElevNode[], st: ElevatorStatus): void {
  for (const entry of sorted) {
    const dot = `<div style="width:5px;height:5px;border-radius:50%;background:${st.color};box-shadow:0 0 4px ${st.color}"></div>`;
    try {
      state.markers.elev.push(
        v.Markers.add(entry.node, dot, {
          rank: 'always-visible',
          placement: 'center',
          occlude: false,
        }),
      );
    } catch {
      // Ignore.
    }
  }
}

function addElevatorBadge(
  v: MappedinMapView,
  top: SortedElevNode,
  name: string,
  st: ElevatorStatus,
): void {
  const badge = `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;cursor:default"><div class="layer-marker" style="display:flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;background:${st.bg};color:#fff;font-size:9px;font-weight:600;white-space:nowrap;box-shadow:0 2px 10px ${st.sh}">${ELEV_ICON}<span class="marker-text">${name}</span><span class="marker-status" style="font-size:8px;opacity:0.85">${st.label}</span></div></div>`;
  try {
    state.markers.elev.push(
      v.Markers.add(top.node, badge, {
        rank: 'always-visible',
        placement: 'top',
        occlude: false,
      }),
    );
  } catch {
    // Ignore.
  }
}

function renderElevator(
  v: MappedinMapView,
  elev: MappedinConnection & { nodes?: ElevNode[] },
  index: number,
  st: ElevatorStatus,
): void {
  const name = escapeHtml(elev.name ?? `Elevator ${index + 1}`);
  const nodes = elev.nodes ?? [];
  if (!nodes.length) return;
  const sorted = sortElevatorNodes(nodes);
  const bottom = sorted[0];
  const top = sorted[sorted.length - 1];
  if (!bottom || !top) return;
  const floorSpan = top.ord - bottom.ord;
  maybeAddShaftShape(v, bottom, floorSpan, st);
  addElevatorDots(v, sorted, st);
  addElevatorBadge(v, top, name, st);
}

function showElevators(): void {
  const { v, d } = handles();
  if (!v || !d) return;
  clearKey('elev');
  let conns: MappedinConnection[] = [];
  try {
    conns = (d.getByType('connection') as MappedinConnection[]) ?? [];
  } catch {
    // Ignore.
  }
  const elevators: Array<MappedinConnection & { nodes?: ElevNode[] }> = [];
  for (const c of conns as Array<MappedinConnection & { nodes?: ElevNode[] }>) {
    const ct = (c.type ?? c.connectionType ?? '').toLowerCase();
    const cn = (c.name ?? '').toLowerCase();
    if (
      ct.includes('elevator') ||
      ct.includes('lift') ||
      cn.includes('elevator') ||
      cn.includes('lift')
    ) {
      elevators.push(c);
    }
  }
  if (!elevators.length) return;
  rebuildFloorLookup();

  const STATUS_OK = {
    label: 'Operational',
    color: '#22c55e',
    bg: 'rgba(34,197,94,0.85)',
    sh: 'rgba(34,197,94,0.4)',
  };
  const STATUS_BUSY = {
    label: 'Very Busy',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.9)',
    sh: 'rgba(245,158,11,0.4)',
  };
  const STATUS_BROKEN = {
    label: 'Out of Order',
    color: '#ef4444',
    bg: 'rgba(239,68,68,0.85)',
    sh: 'rgba(239,68,68,0.4)',
  };

  const brokenIdx = 0;
  let busyIdx = elevators.length > 1 ? 1 : 0;

  elevators.forEach((elev, ei) => {
    const st = ei === brokenIdx ? STATUS_BROKEN : ei === busyIdx ? STATUS_BUSY : STATUS_OK;
    renderElevator(v, elev, ei, st);
    if (ei === elevators.length - 1 && busyIdx === brokenIdx && elevators.length > 1) {
      busyIdx = brokenIdx === 0 ? 1 : 0;
    }
  });
}

function clearGuards(): void {
  state.guardStop = true;
  const mv = handles().v;
  if (mv) {
    for (const g of state.guards) {
      try {
        mv.Markers.remove(g.marker);
      } catch {
        // Ignore.
      }
    }
  }
  state.guards = [];
  state.markers.guard = [];
}

function clearElevators(): void {
  clearKey('elev');
  const mv = handles().v;
  if (mv?.Shapes) {
    for (const shape of state.elevShapes) {
      try {
        mv.Shapes.remove(shape);
      } catch {
        // Ignore.
      }
    }
  }
  state.elevShapes = [];
}

function clearAll(): void {
  state.guardStop = true;
  clearVacancy();
  clearKey('cam');
  clearKey('dir');
  clearKey('guard');
  clearKey('acc');
  clearKey('tmp');
  clearKey('ast');
  clearKey('bluedot');
  clearKey('events');
  clearKey('elev');
  try {
    clearFloorPipes();
  } catch {
    // Ignore.
  }
  const mv = handles().v;
  if (mv?.Shapes) {
    for (const shape of state.elevShapes) {
      try {
        mv.Shapes.remove(shape);
      } catch {
        // Ignore.
      }
    }
  }
  state.elevShapes = [];
}

export const layerManager = {
  loadDirectoryKiosks,
  showCameraMarkers,
  showDirectoryMarkers,
  showGuardMarkers,
  showAccessMarkers,
  showTempMarkers,
  showAssetMarkers,
  prefetchPopularZones,
  showBluedotHeatmap,
  showEvents,
  showVacancy,
  showElevators,
  clearCameras: (): void => clearKey('cam'),
  clearDirectories: (): void => clearKey('dir'),
  clearGuards,
  clearAccess: (): void => clearKey('acc'),
  clearTemps: (): void => clearKey('tmp'),
  clearAssets: (): void => clearKey('ast'),
  clearBluedot: (): void => clearKey('bluedot'),
  clearEvents: (): void => clearKey('events'),
  clearVacancy,
  clearElevators,
  clearAll,
};

export type LayerManager = typeof layerManager;
